import {
  FC, memo, useEffect, useMemo, useRef,
} from 'react';
import clsx from 'clsx';
import {
  DEFAULT_INSPECTION_INTERVAL_HOURS,
  getInspectionSteps,
  getNextInspection,
} from 'pages/Engines/constants/inspectionConstants';
import styles from './EngineInspectionTimeline.module.scss';

interface EngineInspectionTimelineProps {
  /** Текущая наработка машины в эквивалентных часах. */
  currentHours?: number;
  /** Межремонтный интервал машины: шаг между инспекциями. */
  intervalHours?: number;
}

/**
 * Ширина одной инспекции. Должна совпадать с $step-width в модуле стилей:
 * раскладку маркеров делает flexbox, а по этой константе считается только
 * положение зелёного маркера текущей наработки между ними.
 */
const STEP_WIDTH_PX = 150;

const formatHours = (hours: number): string => Math.round(hours).toLocaleString('ru-RU');

export const EngineInspectionTimeline: FC<EngineInspectionTimelineProps> = memo(({
  currentHours,
  intervalHours,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const interval = intervalHours && intervalHours > 0 ? intervalHours : DEFAULT_INSPECTION_INTERVAL_HOURS;
  const hours = currentHours ?? 0;

  const steps = useMemo(() => getInspectionSteps(hours, interval), [hours, interval]);
  const nextInspection = useMemo(() => getNextInspection(hours, interval), [hours, interval]);

  // Маркер инспекции стоит в центре своей ячейки, то есть инспекция n - в точке
  // (n - 0.5) * ширина. Отсюда и положение текущей наработки между инспекциями.
  // При нулевой наработке формула даёт отрицательное смещение - прижимаем к началу.
  const currentOffset = Math.max(0, (hours / interval - 0.5) * STEP_WIDTH_PX);

  // Прокручиваем к текущей наработке: у машины с большой наработкой шкала иначе
  // открывалась бы на инспекциях, пройденных годы назад.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.scrollLeft = Math.max(0, currentOffset - viewport.clientWidth / 2);
  }, [currentOffset]);

  return (
    <div className={styles.timeline}>
      <div className={styles.viewport} ref={viewportRef}>
        <div className={styles.track}>
          {steps.map((step) => (
            <div
              key={step.stepNumber}
              className={clsx(
                styles.step,
                step.stepNumber === nextInspection.stepNumber && styles.nextStep,
                step.hours <= hours && styles.passedStep,
              )}
            >
              <span className={styles.stepMarker}>{step.program}</span>
              <span className={styles.stepHours}>
                {formatHours(step.hours)}
                {' '}
                ч
              </span>
            </div>
          ))}

          {currentHours !== undefined && (
            <div
              className={styles.currentPosition}
              style={{ left: `${currentOffset}px` }}
              title={`Текущая наработка: ${formatHours(hours)} экв. ч`}
            />
          )}
        </div>
      </div>

      <div className={styles.summary}>
        {currentHours === undefined ? (
          <span className={styles.noData}>Нет измерений: текущая наработка неизвестна</span>
        ) : (
          <>
            <span>
              Наработка:
              {' '}
              <strong>{formatHours(hours)}</strong>
              {' '}
              экв. ч
            </span>
            <span>
              Следующая инспекция:
              {' '}
              <strong>{nextInspection.program}</strong>
              {' '}
              при
              {' '}
              {formatHours(nextInspection.hours)}
              {' '}
              ч (осталось
              {' '}
              {formatHours(nextInspection.hours - hours)}
              {' '}
              ч)
            </span>
          </>
        )}
      </div>
    </div>
  );
});

EngineInspectionTimeline.displayName = 'EngineInspectionTimeline';
